import React, { useState, useEffect, useCallback, useRef } from 'react';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, MessageSquare, Clock, Send, ArrowLeft, Paperclip,
  AlertCircle, CheckCircle, Loader2, ChevronDown, Search,
  CircleDot, Timer, UserCheck, AlertTriangle, XCircle, BookOpen
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'text-blue-400', bg: 'bg-blue-500/15', dot: 'bg-blue-500' },
  in_progress: { label: 'In Progress', color: 'text-amber-400', bg: 'bg-amber-500/15', dot: 'bg-amber-500' },
  waiting_for_customer: { label: 'Awaiting Reply', color: 'text-purple-400', bg: 'bg-purple-500/15', dot: 'bg-purple-500' },
  escalated: { label: 'Escalated', color: 'text-red-400', bg: 'bg-red-500/15', dot: 'bg-red-500' },
  resolved: { label: 'Resolved', color: 'text-emerald-400', bg: 'bg-emerald-500/15', dot: 'bg-emerald-500' },
  closed: { label: 'Closed', color: 'text-gray-400', bg: 'bg-gray-500/15', dot: 'bg-gray-500' },
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'text-gray-400', bg: 'bg-gray-500/15' },
  normal: { label: 'Normal', color: 'text-blue-400', bg: 'bg-blue-500/15' },
  high: { label: 'High', color: 'text-amber-400', bg: 'bg-amber-500/15' },
  urgent: { label: 'Urgent', color: 'text-red-400', bg: 'bg-red-500/15' },
};

// ── Status Badge ────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.open;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${c.bg} ${c.color}`} data-testid={`status-${status}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} /> {c.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const c = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${c.bg} ${c.color}`}>
      {c.label}
    </span>
  );
};

// ── Create Ticket Form ──────────────────────────────────
const CreateTicketForm = ({ categories, onCreated, onCancel }) => {
  const [form, setForm] = useState({ subject: '', category: '', priority: 'normal', description: '', related_invoice_id: '', related_transaction_id: '', wallet_address: '' });
  const [submitting, setSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (form.category) {
      (async () => {
        try {
          const { data } = await axios.get(`${API}/kb/suggest`, { params: { ticket_category: form.category } });
          setSuggestions(data.suggestions || []);
        } catch { setSuggestions([]); }
      })();
    } else { setSuggestions([]); }
  }, [form.category]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.category || !form.description.trim()) {
      toast.error('Please fill in subject, category, and description');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.related_invoice_id) delete payload.related_invoice_id;
      if (!payload.related_transaction_id) delete payload.related_transaction_id;
      if (!payload.wallet_address) delete payload.wallet_address;
      const { data } = await axios.post(`${API}/tickets`, payload);
      toast.success(`Ticket ${data.id} created`);
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create ticket');
    } finally { setSubmitting(false); }
  };

  return (
    <Card className="border-border bg-card" data-testid="create-ticket-form">
      <CardContent className="py-6">
        <h2 className="text-lg font-bold text-foreground mb-4">New Support Ticket</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Subject *</label>
            <Input placeholder="Brief summary of your issue" value={form.subject}
              onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} data-testid="ticket-subject" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Category *</label>
              <select className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground"
                value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} data-testid="ticket-category">
                <option value="">Select category...</option>
                {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Priority</label>
              <select className="w-full h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground"
                value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} data-testid="ticket-priority">
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent (Payment Related)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Description *</label>
            <Textarea rows={5} placeholder="Describe your issue in detail..." value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))} data-testid="ticket-description" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Invoice ID (optional)</label>
              <Input placeholder="INV-xxxx" value={form.related_invoice_id}
                onChange={e => setForm(p => ({ ...p, related_invoice_id: e.target.value }))} data-testid="ticket-invoice-id" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Transaction ID (optional)</label>
              <Input placeholder="0x..." value={form.related_transaction_id}
                onChange={e => setForm(p => ({ ...p, related_transaction_id: e.target.value }))} data-testid="ticket-tx-id" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Wallet Address (optional)</label>
              <Input placeholder="0x..." value={form.wallet_address}
                onChange={e => setForm(p => ({ ...p, wallet_address: e.target.value }))} data-testid="ticket-wallet" />
            </div>
          </div>
          {/* KB Suggestions */}
          {suggestions.length > 0 && (
            <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-lg p-3" data-testid="kb-suggestions">
              <p className="text-xs font-semibold text-indigo-400 mb-2">Before submitting, check if these articles help:</p>
              <div className="space-y-1">
                {suggestions.map(a => (
                  <a key={a.id} href={`/knowledge-base`} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-indigo-500/10 transition-colors text-xs text-foreground group"
                    data-testid={`suggest-${a.id}`}>
                    <BookOpen className="h-3 w-3 text-indigo-400 shrink-0" />
                    <span className="group-hover:text-indigo-400 transition-colors">{a.title}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-ticket-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Submit Ticket
            </Button>
            <Button type="button" variant="ghost" onClick={onCancel} data-testid="cancel-ticket-btn">Cancel</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ── Ticket Conversation View ────────────────────────────
const TicketConversation = ({ ticketId, onBack }) => {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sla, setSla] = useState(null);
  const [linked, setLinked] = useState({});
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [csat, setCsat] = useState(null);
  const [csatRating, setCsatRating] = useState(0);
  const [csatComment, setCsatComment] = useState('');
  const [csatSubmitting, setCsatSubmitting] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchTicket = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tickets/${ticketId}`);
      setTicket(data.ticket);
      setMessages(data.messages);
      setSla(data.sla);
      setLinked(data.linked || {});
    } catch { toast.error('Failed to load ticket'); }
    setLoading(false);
  }, [ticketId]);

  const fetchCsat = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tickets/${ticketId}/csat`);
      setCsat(data.csat);
    } catch {}
  }, [ticketId]);

  useEffect(() => { fetchTicket(); fetchCsat(); }, [fetchTicket, fetchCsat]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/tickets/${ticketId}/reply`, { message: reply.trim() });
      setReply('');
      fetchTicket();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reply');
    } finally { setSending(false); }
  };

  const handleCsatSubmit = async () => {
    if (csatRating < 1) { toast.error('Please select a rating'); return; }
    setCsatSubmitting(true);
    try {
      await axios.post(`${API}/tickets/${ticketId}/csat`, { rating: csatRating, comment: csatComment });
      toast.success('Thank you for your feedback!');
      fetchCsat();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    setCsatSubmitting(false);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!ticket) return <div className="text-center py-12 text-muted-foreground">Ticket not found</div>;

  const sc = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;

  return (
    <div className="space-y-4" data-testid="ticket-conversation">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-list"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
      </div>
      <Card className="border-border bg-card">
        <CardContent className="py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-mono text-muted-foreground mb-1">{ticket.id}</p>
              <h2 className="text-lg font-bold text-foreground">{ticket.subject}</h2>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <StatusBadge status={ticket.status} />
                <PriorityBadge priority={ticket.priority} />
                <span className="text-xs text-muted-foreground">{new Date(ticket.created_at).toLocaleString()}</span>
              </div>
            </div>
          </div>
          {(ticket.related_invoice_id || ticket.related_transaction_id || ticket.wallet_address) && (
            <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
              {ticket.related_invoice_id && <span className="px-2 py-1 bg-muted/30 rounded">Invoice: {ticket.related_invoice_id}</span>}
              {ticket.related_transaction_id && <span className="px-2 py-1 bg-muted/30 rounded font-mono">TX: {ticket.related_transaction_id.slice(0, 16)}...</span>}
              {ticket.wallet_address && <span className="px-2 py-1 bg-muted/30 rounded font-mono">Wallet: {ticket.wallet_address.slice(0, 16)}...</span>}
            </div>
          )}
          {/* SLA Status */}
          {sla && (
            <div className="mt-3 flex items-center gap-3 text-xs">
              <span className={`px-2 py-1 rounded ${sla.first_response_breached ? 'bg-red-500/15 text-red-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                {sla.first_response_breached ? 'SLA Breached: ' : ''}First Response: {sla.first_response_elapsed_hours !== null ? `${sla.first_response_elapsed_hours}h` : 'Pending'} / {sla.first_response_target_hours}h
              </span>
              <span className={`px-2 py-1 rounded ${sla.resolution_breached ? 'bg-red-500/15 text-red-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                {sla.resolution_breached ? 'SLA Breached: ' : ''}Resolution: {sla.resolution_elapsed_hours !== null ? `${sla.resolution_elapsed_hours}h` : 'Pending'} / {sla.resolution_target_hours}h
              </span>
            </div>
          )}
          {/* Linked Records */}
          {(linked.invoice || linked.transaction) && (
            <div className="mt-3 space-y-2">
              {linked.invoice && (
                <div className="px-3 py-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-xs" data-testid="linked-invoice">
                  <span className="font-semibold text-indigo-400">Linked Invoice: </span>
                  <span className="text-foreground">{linked.invoice.id}</span>
                  <span className="text-muted-foreground ml-2">Amount: ${linked.invoice.amount || linked.invoice.amount_due}</span>
                  <span className="text-muted-foreground ml-2">Status: {linked.invoice.status}</span>
                </div>
              )}
              {linked.transaction && (
                <div className="px-3 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-xs" data-testid="linked-transaction">
                  <span className="font-semibold text-cyan-400">Linked TX: </span>
                  <span className="text-foreground font-mono">{(linked.transaction.tx_hash || '').slice(0, 24)}...</span>
                  <span className="text-muted-foreground ml-2">Amount: {linked.transaction.amount} {linked.transaction.currency}</span>
                  <span className="text-muted-foreground ml-2">Status: {linked.transaction.status}</span>
                  {linked.transaction.confirmations != null && <span className="text-muted-foreground ml-2">Confirmations: {linked.transaction.confirmations}</span>}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Messages */}
      <Card className="border-border bg-card">
        <CardContent className="py-4">
          <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
            {messages.map(msg => {
              const isCustomer = msg.sender_role === 'customer';
              return (
                <div key={msg.id} className={`flex ${isCustomer ? 'justify-end' : 'justify-start'}`} data-testid={`message-${msg.id}`}>
                  <div className={`max-w-[75%] rounded-xl px-4 py-3 ${isCustomer ? 'bg-indigo-600/20 border border-indigo-500/20' : 'bg-muted/30 border border-border'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold ${isCustomer ? 'text-indigo-400' : 'text-emerald-400'}`}>
                        {isCustomer ? 'You' : msg.sender_name || 'Support'}
                      </span>
                      <span className="text-[10px] text-muted-foreground">{new Date(msg.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{msg.message}</p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply Box */}
          {ticket.status !== 'closed' && (
            <div className="mt-4 pt-4 border-t border-border">
              <Textarea rows={3} placeholder="Type your reply..." value={reply}
                onChange={e => setReply(e.target.value)} data-testid="ticket-reply-input"
                onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) handleSendReply(); }} />
              <div className="flex justify-end mt-2">
                <Button onClick={handleSendReply} disabled={sending || !reply.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="send-reply-btn">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                  Send Reply
                </Button>
              </div>
            </div>
          )}
          {ticket.status === 'closed' && (
            <div className="mt-4 pt-4 border-t border-border text-center text-sm text-muted-foreground">
              This ticket has been closed. Create a new ticket if you need further assistance.
            </div>
          )}
        </CardContent>
      </Card>

      {/* CSAT Survey */}
      {['resolved', 'closed'].includes(ticket.status) && (
        <Card className="border-border bg-card" data-testid="csat-section">
          <CardContent className="py-4">
            {csat ? (
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground mb-2">Your Feedback</p>
                <div className="flex justify-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map(s => (
                    <span key={s} className={`text-2xl ${s <= csat.rating ? 'text-amber-400' : 'text-muted-foreground/30'}`}>&#9733;</span>
                  ))}
                </div>
                {csat.comment && <p className="text-xs text-muted-foreground italic">"{csat.comment}"</p>}
                <p className="text-[10px] text-muted-foreground mt-1">Thank you for your feedback!</p>
              </div>
            ) : (
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground mb-1">How was your experience?</p>
                <p className="text-xs text-muted-foreground mb-3">Rate the support you received for this ticket</p>
                <div className="flex justify-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map(s => (
                    <button key={s} onClick={() => setCsatRating(s)}
                      className={`text-3xl transition-transform hover:scale-110 ${s <= csatRating ? 'text-amber-400' : 'text-muted-foreground/30 hover:text-amber-400/50'}`}
                      data-testid={`csat-star-${s}`}>&#9733;</button>
                  ))}
                </div>
                {csatRating > 0 && (
                  <div className="max-w-md mx-auto">
                    <Textarea rows={2} placeholder="Any additional comments? (optional)" value={csatComment}
                      onChange={e => setCsatComment(e.target.value)} className="text-xs mb-2" data-testid="csat-comment" />
                    <Button size="sm" onClick={handleCsatSubmit} disabled={csatSubmitting} className="bg-amber-600 hover:bg-amber-700" data-testid="csat-submit">
                      {csatSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                      Submit Feedback
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ── Main Support Page ───────────────────────────────────
const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'create' | 'conversation'
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filter, setFilter] = useState('all');

  const fetchTickets = useCallback(async () => {
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      const { data } = await axios.get(`${API}/tickets`, { params });
      setTickets(data.data || []);
    } catch {}
    setLoading(false);
  }, [filter]);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tickets/categories`);
      setCategories(data.categories || []);
    } catch {}
  }, []);

  useEffect(() => { fetchTickets(); fetchCategories(); }, [fetchTickets, fetchCategories]);

  const openCount = tickets.filter(t => !['resolved', 'closed'].includes(t.status)).length;

  if (view === 'create') {
    return (
      <MerchantLayout title="New Ticket">
        <div className="p-6 max-w-3xl mx-auto">
          <CreateTicketForm categories={categories} onCancel={() => setView('list')}
            onCreated={(t) => { setView('conversation'); setSelectedTicket(t.id); fetchTickets(); }} />
        </div>
      </MerchantLayout>
    );
  }

  if (view === 'conversation' && selectedTicket) {
    return (
      <MerchantLayout title="Ticket">
        <div className="p-6 max-w-3xl mx-auto">
          <TicketConversation ticketId={selectedTicket} onBack={() => { setView('list'); setSelectedTicket(null); fetchTickets(); }} />
        </div>
      </MerchantLayout>
    );
  }

  return (
    <MerchantLayout title="Support">
      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Get help from our team</p>
          <Button onClick={() => setView('create')} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-ticket-btn">
            <Plus className="h-4 w-4 mr-1" /> New Ticket
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-border bg-card"><CardContent className="py-3 text-center">
            <p className="text-2xl font-bold text-foreground">{tickets.length}</p>
            <p className="text-[11px] text-muted-foreground">Total Tickets</p>
          </CardContent></Card>
          <Card className="border-border bg-card"><CardContent className="py-3 text-center">
            <p className="text-2xl font-bold text-blue-400">{openCount}</p>
            <p className="text-[11px] text-muted-foreground">Open</p>
          </CardContent></Card>
          <Card className="border-border bg-card"><CardContent className="py-3 text-center">
            <p className="text-2xl font-bold text-purple-400">{tickets.filter(t => t.status === 'waiting_for_customer').length}</p>
            <p className="text-[11px] text-muted-foreground">Awaiting Reply</p>
          </CardContent></Card>
          <Card className="border-border bg-card"><CardContent className="py-3 text-center">
            <p className="text-2xl font-bold text-emerald-400">{tickets.filter(t => t.status === 'resolved').length}</p>
            <p className="text-[11px] text-muted-foreground">Resolved</p>
          </CardContent></Card>
        </div>

        {/* Filter */}
        <div className="flex gap-1 bg-[hsl(var(--card))] border border-border rounded-lg p-1 w-fit">
          {[['all', 'All'], ['open', 'Open'], ['in_progress', 'In Progress'], ['waiting_for_customer', 'Awaiting Reply'], ['resolved', 'Resolved'], ['closed', 'Closed']].map(([v, l]) => (
            <button key={v} className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${filter === v ? 'bg-indigo-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setFilter(v)} data-testid={`filter-${v}`}>{l}</button>
          ))}
        </div>

        {/* Ticket List */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : tickets.length === 0 ? (
          <Card className="border-border bg-card"><CardContent className="py-12 text-center">
            <MessageSquare className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No tickets yet. Create one to get help.</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {tickets.map(t => (
              <Card key={t.id} className="border-border bg-card hover:border-indigo-500/30 transition-colors cursor-pointer"
                onClick={() => { setSelectedTicket(t.id); setView('conversation'); }} data-testid={`ticket-row-${t.id}`}>
                <CardContent className="py-3 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] font-mono text-muted-foreground">{t.id}</span>
                      <StatusBadge status={t.status} />
                      <PriorityBadge priority={t.priority} />
                    </div>
                    <p className="text-sm font-medium text-foreground truncate">{t.subject}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t.category?.replace(/_/g, ' ')} — Last reply {t.last_reply_by === 'admin' ? 'by Support' : 'by You'} {new Date(t.last_reply_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-muted-foreground">{t.message_count} message{t.message_count !== 1 ? 's' : ''}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(t.created_at).toLocaleDateString()}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </MerchantLayout>
  );
};

export default Support;
